import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react"

import {
  BeaconImpressionObserver,
  ImpressionModule,
} from "@app/scripts/lib/beacon-impression"
import {
  BeaconProductResult,
  trackClickthrough,
  trackRender,
} from "@app/scripts/lib/beacon"

/**
 * Results tracking context for Beacon 2.0
 * ---
 * Provides the `responseId` and `module` (search, category, autocomplete,
 * recommendations) to product cards so they can fire clickthrough and
 * impression events tied to the correct Searchspring API response.
 *
 * A render event is fired automatically when a new responseId arrives.
 */

type BeaconResultsContextValue = {
  module: ImpressionModule | null
  responseId: string | null
  tag?: string
  observeProduct: (el: Element, result: BeaconProductResult) => void
  unobserveProduct: (el: Element) => void
  reportClickthrough: (result: BeaconProductResult) => void
}

const noop = () => {}

const defaultValue: BeaconResultsContextValue = {
  module: null,
  responseId: null,
  observeProduct: noop,
  unobserveProduct: noop,
  reportClickthrough: noop,
}

const BeaconResultsContext =
  createContext<BeaconResultsContextValue>(defaultValue)

export function useBeaconResults() {
  return useContext(BeaconResultsContext)
}

type ProviderProps = {
  module: ImpressionModule
  responseId: string | null | undefined
  tag?: string
  children: ReactNode
}

export function BeaconResultsProvider({
  module,
  responseId,
  tag,
  children,
}: ProviderProps) {
  const observerRef = useRef<BeaconImpressionObserver | null>(null)
  const renderedIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!responseId) return

    if (renderedIdRef.current !== responseId) {
      renderedIdRef.current = responseId
      trackRender(module, responseId, { tag })
    }

    observerRef.current?.disconnect()
    observerRef.current = new BeaconImpressionObserver({
      module,
      responseId,
      tag,
    })

    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    }
  }, [module, responseId, tag])

  const value = useMemo<BeaconResultsContextValue>(
    () => ({
      module,
      responseId: responseId ?? null,
      tag,
      observeProduct: (el, result) => {
        observerRef.current?.observeProduct(el, result)
      },
      unobserveProduct: () => {
        // IntersectionObserver automatically handles element removal; no-op
      },
      reportClickthrough: (result) => {
        if (!responseId) return
        trackClickthrough(module, responseId, result, { tag })
      },
    }),
    [module, responseId, tag]
  )

  return (
    <BeaconResultsContext.Provider value={value}>
      {children}
    </BeaconResultsContext.Provider>
  )
}
